<script setup lang="ts">
import { computed, h } from 'vue';

interface Props {
  heading?: string;
  subHeading?: string;
  containerClass?: string;
  headingClass?: string;
  subHeadingClass?: string;
  icon?: string | object; // Can be string (SVG/HTML) or component
  headingLevel?: string;
  subHeadingLevel?: string;
  padding?: boolean;
}

const { 
  heading = '',
  subHeading = '',
  containerClass = '',
  headingClass = '',
  subHeadingClass = '',
  icon = '',
  headingLevel = 'h1',
  subHeadingLevel = 'h3',
  padding = true
} = defineProps<Props>();

// Convert heading level to proper tag name
const getHeadingTag = (level: string): string => {
  const validLevels = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
  const levelLower = level.toLowerCase();
  
  if (validLevels.includes(levelLower)) {
    return levelLower;
  }
  
  const numericLevel = parseInt(level);
  if (!isNaN(numericLevel) && numericLevel >= 1 && numericLevel <= 6) {
    return `h${numericLevel}`;
  }
  
  return 'h1';
};

// Font sizes based on heading level
const headingSizes: Record<string, string> = {
  'h1': 'text-3xl sm:text-4xl',
  'h2': 'text-2xl sm:text-3xl',
  'h3': 'text-xl sm:text-2xl',
  'h4': 'text-lg sm:text-xl',
  'h5': 'text-base sm:text-lg',
  'h6': 'text-sm sm:text-base'
};

const headingTag = computed(() => getHeadingTag(headingLevel));
const headingSizeClass = computed(() => headingSizes[headingTag.value] || 'text-lg sm:text-xl');

// Combined heading classes
const headingClasses = computed(() => {
  const baseClass = 'flex items-center gap-3';
  return [baseClass, headingSizeClass.value, headingClass].filter(Boolean).join(' ');
});

// Render icon
const renderIcon = computed(() => {
  if (!icon) return null;
  
  const iconClasses = ['inline-block align-middle', { 'w-[1em] h-[1em]': typeof icon !== 'string' }];
  
  if (typeof icon === 'string') {
    // If it's an SVG string
    if (icon.includes('<svg')) {
      return h('span', { 
        class: iconClasses,
        innerHTML: icon,
        'aria-hidden': 'true'
      });
    }
    // If it's a simple text icon
    return h('span', { 
      class: iconClasses,
      innerHTML: icon,
      'aria-hidden': 'true'
    });
  }
  
  // If it's a component
  return h(icon, { 
    class: iconClasses,
    'aria-hidden': 'true'
  });
});

// Add computed for padding classes
const paddingClasses = computed(() => padding ? 'pt-6 pb-6' : '');
</script>

<template>
  <div 
    :class="[containerClass, 'flex flex-col justify-center', paddingClasses]" 
    role="region"
    :aria-labelledby="heading ? 'component-heading' : undefined" 
    :aria-describedby="subHeading ? 'component-subheading' : undefined"
  >
    <component
      :is="headingTag"
      :class="headingClasses" 
      id="component-heading"
    >
      <component :is="renderIcon" />
      {{ heading }}
    </component>
    
    <component
      :is="getHeadingTag(subHeadingLevel)"
      v-if="subHeading" 
      :class="[subHeadingClass, 'w-full flex-1 font-light']" 
      id="component-subheading"
    >
      {{ subHeading }}
    </component>
    
    <slot />
  </div>
</template>